import { BarChart2, BookOpen, LogOut, Plus, Trophy, Users } from "lucide-react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { cn } from "@/utils/cn";
import { useState } from "react";
import { NewSessionModal } from "@/components/sessions/NewSessionModal";
import { useAuth } from "@/auth/AuthContext";
import { Avatar } from "@/components/ui/Avatar";
import { SignOutButton } from "./SignOutButton";

interface SideNavProps {
  pendingRequests: number;
  hasUnreadComments: boolean;
}

export function SideNav({ pendingRequests, hasUnreadComments }: SideNavProps) {
  const [showNewSession, setShowNewSession] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const navItem = (to: string, icon: React.ReactNode, label: string, testId?: string) => (
    <NavLink
      to={to}
      data-testid={testId}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors",
          isActive ? "text-accent bg-accent/10" : "text-text-muted hover:text-text-base hover:bg-white/5"
        )
      }
    >
      {icon}
      <span>{label}</span>
    </NavLink>
  );

  return (
    <>
      <aside className="hidden md:flex flex-col w-56 shrink-0 sticky top-0 h-dvh border-r border-white/10 bg-bg-base px-3 py-6 gap-1 overflow-y-auto">
        <div className="flex items-center gap-2 px-3 mb-6">
          <img src="/logo.png" alt="Sendstats" className="w-8 h-8" />
          <span className="font-display text-xl tracking-wider leading-none">
            <span className="text-accent">SEND</span>
            <span className="text-text-base">STATS</span>
          </span>
        </div>

        <button
          onClick={() => setShowNewSession(true)}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-accent text-white text-sm font-medium mb-4 hover:bg-accent/90 transition-colors"
          data-testid="new-session-button"
        >
          <Plus size={18} strokeWidth={2.5} />
          Log a session
        </button>

        {navItem("/logbook", <BookOpen size={18} />, "Logbook", "nav-logbook")}
        {navItem("/stats", <BarChart2 size={18} />, "Stats", "nav-stats")}
        {navItem("/challenges", <Trophy size={18} />, "Challenges")}

        <NavLink
          to="/crew"
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors",
              isActive ? "text-accent bg-accent/10" : "text-text-muted hover:text-text-base hover:bg-white/5"
            )
          }
        >
          <div className="relative">
            <Users size={18} />
            {pendingRequests > 0 && (
              <span className="absolute -top-1 -right-1.5 w-4 h-4 rounded-full bg-accent text-white text-[9px] font-bold flex items-center justify-center">
                {pendingRequests > 9 ? "9+" : pendingRequests}
              </span>
            )}
            {hasUnreadComments && pendingRequests === 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-accent" />
            )}
          </div>
          <span>Crew</span>
        </NavLink>

        {/* Pinned user section */}
        <div className="mt-auto pt-4 border-t border-white/10 flex items-center justify-between gap-2">
          <Link
            to="/profile"
            className="flex items-center gap-2 min-w-0 hover:opacity-80 transition-opacity"
          >
            <Avatar photoPath={user?.photo_path ?? null} handle={user?.handle ?? ""} size={32} />
            <span className="text-sm text-text-muted truncate">@{user?.handle}</span>
          </Link>
          <SignOutButton
            title="Sign out"
            className="shrink-0 text-text-muted hover:text-red-400 transition-colors"
            data-testid="logout-button"
          >
            <LogOut size={16} />
          </SignOutButton>
        </div>
      </aside>

      {showNewSession && (
        <NewSessionModal
          onClose={() => setShowNewSession(false)}
          onCreated={(id) => {
            setShowNewSession(false);
            navigate(`/sessions/${id}`);
          }}
        />
      )}
    </>
  );
}
